import { graphql } from '../../gql';
import { Box, Button, IconButton, Stack, Typography, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { PlusOutlined } from '@ant-design/icons';
import { FC } from 'react';
import { useNavigate } from 'react-router';
import { useApiMutation, useApiQuery } from '../../query/hooks';
import { mutationResponseHandler } from '../../utils/mutationResponseHandler';
import { DeleteOutline, FileDownloadOutlined, MoreHoriz } from '@mui/icons-material';
import { CustomPopover } from '../../ui/CustomPopover';
import { ActionFiltersButton } from './ActionFiltersButton';
import { useSnackbar } from '../../store/snackbar';

const actionHeader_listActions = graphql(`
	query actionHeader_listActions {
		listAction {
			id
		}
	}
`);

export const actionListHeader_createAction = graphql(`
	mutation actionListHeader_createAction(
		$name: String!
		$description: String
		$priority: ActionPriority!
		$status: ActionStatus!
		$links: Json!
	) {
		createAction(data: { name: $name, description: $description, priority: $priority, status: $status, links: $links }) {
			ok
			errorMessage
			node {
				id
			}
		}
	}
`);

export const ActionListHeader: FC = () => {
	const intl = useIntl();
	const theme = useTheme();
	const navigate = useNavigate();
	const { openSnackbar } = useSnackbar();

	const { data } = useApiQuery(actionHeader_listActions);
	const { mutate: createAction, isPending: isCreatePending } = useApiMutation(actionListHeader_createAction);

	const handleCreateAction = () => {
		createAction(
			{
				name: intl.formatMessage({ id: 'action.detail.name.placeholder' }),
				description: '',
				priority: 'medium',
				status: 'open',
				links: { values: [] },
			},
			{
				onSuccess: (result) => {
					mutationResponseHandler({
						result: result.createAction.ok,
						toastMessages: {
							success: intl.formatMessage({ id: 'action.list.create.success' }),
							error: intl.formatMessage({ id: 'action.list.create.error' }),
						},
						errorMessage: result.createAction.errorMessage,
						callback: () => navigate(`/actions/${result.createAction.node?.id}/detail`),
						openSnackbar,
					});
				},
			},
		);
	};

	return (
		<Stack direction={'row'} justifyContent={'space-between'}>
			<Stack direction={'row'} alignItems={'center'} gap={1}>
				<Typography variant={'h3'} fontWeight={700}>
					<FormattedMessage id={'action.list.header.title'} />
				</Typography>
				{data != null && (
					<Box color={theme.palette.grey[500]} fontSize={14} mt={0.8}>
						{data.listAction.length}
					</Box>
				)}
			</Stack>
			<Stack direction={'row'} alignItems={'stretch'} gap={1}>
				<ActionFiltersButton />
				<Button
					variant={'contained'}
					startIcon={<PlusOutlined />}
					sx={{ display: { xs: 'none', sm: 'flex' } }}
					onClick={handleCreateAction}
					disabled={isCreatePending}
				>
					<FormattedMessage id={'action.list.header.create-button'} />
				</Button>
				<IconButton
					sx={{ bgcolor: theme.palette.primary[400], display: { xs: 'flex', sm: 'none' } }}
					onClick={handleCreateAction}
					disabled={isCreatePending}
				>
					<PlusOutlined />
				</IconButton>
			</Stack>
		</Stack>
	);
};
